// React imports
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";

// Import Redux actions
import { setActiveLink } from "./NavigationSlice_Store";
// Import typed Redux hooks
import { useAppDispatch, useAppSelector } from "./hooks/reduxHooks";

// Import navigation data (currently unused - keeping for future use)
// import SidebarNavigationData from "./SidebarNavigationData_JsonFile.json";

// Import icons from heroicons
import {
  HomeIcon,
  BuildingLibraryIcon,
  UsersIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  BookmarkIcon,
  FunnelIcon,
  HeartIcon,
  ChartBarIcon,
  DocumentTextIcon,
  CogIcon,
  ClockIcon,
  PencilIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  PlusIcon,
  BeakerIcon,
  EllipsisHorizontalIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

import ChatContextMenu from "./components/ChatContextMenu_Component";
import RenameChatModal from "./components/RenameChatModal_Component";
import DeleteChatModal from "./components/DeleteChatModal_Component";
import SearchChatsModal from "./components/SearchChatsModal_Component";

import Tooltip from "./components/Tooltip";

// Circular plus icon (blue circle with white plus)
const CirclePlusIcon = ({ className = "" }: { className?: string }) => (
  <div
    className={`flex items-center justify-center rounded-full ${className}`}
    style={{ backgroundColor: "#234E81" }}
  >
    <PlusIcon className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
  </div>
);

// Import logo
import logoImage from "../assets/logo.png";

interface SidebarProject {
  id: string;
  name?: string;
}

interface SidebarComponentProps {
  isCollapsed?: boolean;
  onCollapseToggle?: (collapsed: boolean) => void;
  onStartProject?: () => void;
  onClose?: () => void;
  isWorkspaceForcingCollapse?: boolean;
  projects?: SidebarProject[];
  currentProjectId?: string | null;
  onProjectClick?: (projectId: string) => void;
  projectsLoading?: boolean;
}

// Default export of Sidebar_Component
export default function Sidebar_Component({
  isCollapsed = false,
  onCollapseToggle,
  onStartProject,
  onClose,
  isWorkspaceForcingCollapse = false,
  projects = [],
  currentProjectId = null,
  onProjectClick,
  projectsLoading = false,
}: SidebarComponentProps): JSX.Element {

  // Redux state and dispatch
  const dispatch = useAppDispatch();
  const activeLink = useAppSelector((state) => state.navigationSlice.activeLink);
  const conversationId = useAppSelector((state) => state.chatSlice.conversationId);
  const isAdmin = useAppSelector((state) => state.userAuthAndAdminSlice.isAdmin);

  // Get current location for URL sync
  const location = useLocation();

  // Icon mapping for navigation items - using icon strings from SidebarNavigationData as keys
  const iconMap: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
    dashboard: HomeIcon,
    "currency-dollar": CurrencyDollarIcon,
    "building-library": BuildingLibraryIcon,
    users: UsersIcon,
    "user-group": UserGroupIcon,
    bookmark: BookmarkIcon,
    funnel: FunnelIcon,
    heart: HeartIcon,
    "chart-bar": ChartBarIcon,
    "document-text": DocumentTextIcon,
    cog: CogIcon,
    clock: ClockIcon,
    pencil: PencilIcon,
  };

  // Route mapping for navigation items
  const routeMap: Record<string, string> = {
    "Dashboard": "/",
    "Deals Discovery": "/deals",
    "Draft Editor": "/draft",
  };

  // Sync URL changes with Redux state
  useEffect(() => {
    // Map pathname to navigation labels
    const pathToLabel: Record<string, string> = {
      "/": "Dashboard",
      "/deals": "Deals Discovery",
      "/draft": "Draft Editor",
    };

    const labelForPath = pathToLabel[location.pathname];
    if (labelForPath && labelForPath !== activeLink) {
      dispatch(setActiveLink(labelForPath));
    }
  }, [location.pathname, activeLink, dispatch]);

  // Function to get the appropriate icon component based on icon string
  const getIcon = (iconString: string): React.ComponentType<React.SVGProps<SVGSVGElement>> => {
    return iconMap[iconString] || HomeIcon;
  };

  // Function to render navigation items
  const renderNavigationItem = (item: { label: string; icon: string }): JSX.Element => {
    const route = routeMap[item.label] || "/coming-soon";

    // Determine if this item should be highlighted based on Redux state
    const isHighlighted = item.label === activeLink;

    // Handle click to dispatch Redux action
    const handleClick = (): void => {
      dispatch(setActiveLink(item.label));
      // Close mobile sidebar when item is clicked
      if (onClose) {
        onClose();
      }
    };

    // Get the icon component using the icon string from SidebarNavigationData
    const IconComponent = getIcon(item.icon);

    const itemContent = (
      <div
        className={`z-50 flex items-center px-4 py-3 text-sm cursor-pointer hover:bg-gray-100 transition-colors duration-200 ${
          isCollapsed ? "justify-center" : ""
        } ${
          isHighlighted
            ? "bg-blue-50 text-cvc-blue font-bold border-r-2 border-cvc-blue"
            : "text-gray-700"
        }`}
      >
        <IconComponent
          className={`h-4 w-4 ${isCollapsed ? "" : "mr-3"} ${
            isHighlighted ? "text-cvc-blue" : "text-gray-500"
          }`}
          aria-label={item.label}
        />
        {!isCollapsed && <span>{item.label}</span>}
      </div>
    );

    // All items now have a route (either specific or coming-soon)
    return (
      <Link
        key={item.label}
        to={route}
        className="block z-50"
        onClick={handleClick}
      >
        {itemContent}
      </Link>
    );
  };

  // Function to render section (currently unused - keeping for future use)
  // const renderSection = (title: string, items: Array<{ label: string; icon: string }>, _sectionType: string = "default"): JSX.Element => {
  //   return (
  //     <div className="mb-8">
  //       {!isCollapsed && (
  //         <h3 className="px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
  //           {title}
  //         </h3>
  //       )}
  //       <nav className="space-y-1">
  //         {items.map((item) => {
  //           return renderNavigationItem(item);
  //         })}
  //       </nav>
  //     </div>
  //   );
  // };

  // Context menu and rename modal state
  const [openMenuProjectId, setOpenMenuProjectId] = React.useState<string | null>(null);
  const [menuPosition, setMenuPosition] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [renameTarget, setRenameTarget] = React.useState<{ id: string; name: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<{ id: string; name: string } | null>(null);

  const [isSearchModalOpen, setIsSearchModalOpen] = React.useState<boolean>(false);
  const [isHoveringCollapsed, setIsHoveringCollapsed] = React.useState<boolean>(false);

  // Handle click on collapsed sidebar to expand
  const handleCollapsedSidebarClick = (e: React.MouseEvent<HTMLElement>): void => {
    // Don't expand if clicking on a button or interactive element
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    // Allow expansion even in workspace mode - user action should override auto-collapse
    if (isCollapsed && onCollapseToggle) {
      onCollapseToggle(false);
    }
  };

  // Handle navigation to admin/auditing interface in new tab
  const handleViewInAdmin = (): void => {
    if (conversationId) {
      const adminUrl = `/admin/conversation/${conversationId}`;
      window.open(adminUrl, '_blank', 'noopener,noreferrer');
      // Close mobile sidebar when navigating
      if (onClose) {
        onClose();
      }
    }
  };

  return (
    <aside
      className={`bg-white h-full border-r border-gray-200 flex flex-col transition-all duration-200 ${
        isCollapsed ? "w-20 cursor-e-resize" : "w-64"
      }`}
      onClick={handleCollapsedSidebarClick}
      onMouseEnter={() => isCollapsed && setIsHoveringCollapsed(true)}
      onMouseLeave={() => setIsHoveringCollapsed(false)}
    >
      {/* Brand/Header Block */}
      <div className="px-4 py-2.5 border-b border-gray-200">
        <div className="flex items-center justify-between min-h-[3.5rem]">
          {isCollapsed ? (
            <button
              type="button"
              className="p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200 mx-auto"
              aria-label="Expand sidebar"
              title="Expand sidebar"
              onClick={(e) => {
                e.stopPropagation();
                onCollapseToggle && onCollapseToggle(false);
              }}
            >
              <ChevronDoubleRightIcon className="h-5 w-5" style={{ color: "#234E81" }} />
            </button>
          ) : (
            <>
              <a
                href={window.location.origin}
                onClick={(e) => {
                  if (e.ctrlKey || e.metaKey) {
                    return;
                  }
                  e.preventDefault();
                  onStartProject && onStartProject();
                }}
                className="flex items-center space-x-3 hover:opacity-70 transition-opacity duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                aria-label="Start new chat"
              >
                <img src={logoImage} alt="DealOS Logo" className="h-14 w-14 object-contain" />
                <span className="text-gray-300 text-2xl font-light">|</span>
                <span className="text-xl font-cvcBody font-normal text-cvc-text">
                  DealOS
                </span>
              </a>

              {/* Desktop collapse toggle button - only shown when expanded */}
              {onCollapseToggle && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCollapseToggle(true);
                  }}
                  disabled={isWorkspaceForcingCollapse}
                  className={`p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200 ${
                    isWorkspaceForcingCollapse ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  aria-label="Collapse sidebar"
                  title={
                    isWorkspaceForcingCollapse
                      ? "Workspace view auto-hides sidebar"
                      : "Collapse sidebar"
                  }
                >
                  <ChevronDoubleLeftIcon className="h-5 w-5" style={{ color: "#234E81" }} />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Scrollable Navigation Sections */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden py-6 min-h-0">
        {/* Chats section */}
        <div className="px-4">
          {!isCollapsed && (
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-cvcBody font-normal text-gray-500">
                Chats
              </h3>
              <button
                onClick={() => setIsSearchModalOpen(true)}
                className="p-1 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                aria-label="Search chats"
              >
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
          )}

          {/* New Chat Button */}
          {isCollapsed ? (
            <Tooltip content="New chat">
              <a
                href={window.location.origin}
                onClick={(e) => {
                  // Ctrl+click or Cmd+click opens in new tab (default browser behavior for <a>)
                  if (e.ctrlKey || e.metaKey) {
                    return;
                  }
                  // Regular click - prevent navigation and use the callback
                  e.preventDefault();
                  onStartProject && onStartProject();
                }}
                className="w-full aspect-square flex items-center justify-center p-2 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                aria-label="New Chat"
              >
                <CirclePlusIcon className="h-6 w-6" />
              </a>
            </Tooltip>
          ) : (
            <a
              href={window.location.origin}
              onClick={(e) => {
                // Ctrl+click or Cmd+click opens in new tab (default browser behavior for <a>)
                if (e.ctrlKey || e.metaKey) {
                  return;
                }
                // Regular click - prevent navigation and use the callback
                e.preventDefault();
                onStartProject && onStartProject();
              }}
              className="w-full flex items-center gap-3 px-3 py-2 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
            >
              <CirclePlusIcon className="h-6 w-6" />
              <span className="text-sm font-medium" style={{ color: "#234E81" }}>New chat</span>
            </a>
          )}

          {/* Chats List - only show when not collapsed */}
          {!isCollapsed && (
            <div className="mt-4">
              {projectsLoading && (
                <p className="text-xs text-gray-400">Loading chats...</p>
              )}

              {!projectsLoading && projects.length === 0 && (
                <p className="text-xs text-gray-400">
                  Your chats will appear here.
                </p>
              )}

              {!projectsLoading && projects.length > 0 && (
                <nav className="space-y-1">
                  {projects.map((project) => {
                    const isActive = project.id === currentProjectId;
                    const projectUrl = `${window.location.origin}?projectId=${project.id}`;
                    const displayName = project.name || `Project ${project.id.slice(0, 8)}`;

                    return (
                      <div key={project.id} className="group relative">
                        <a
                          href={projectUrl}
                          onClick={(e) => {
                            if (e.ctrlKey || e.metaKey) {
                              return;
                            }
                            e.preventDefault();
                            onProjectClick && onProjectClick(project.id);
                          }}
                          className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors duration-200 touch-manipulation ${
                            isActive
                              ? "bg-blue-50 font-medium"
                              : "hover:bg-gray-100"
                          }`}
                          style={{ color: "#234E81" }}
                        >
                          <span
                            className="truncate flex-1"
                            title={displayName}
                          >
                            {displayName}
                          </span>
                        </a>

                        {/* Ellipsis menu button - appears on hover */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            setMenuPosition({ top: rect.bottom + 4, left: rect.left });
                            setOpenMenuProjectId(openMenuProjectId === project.id ? null : project.id);
                          }}
                          className={`absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-200 focus:outline-none transition-opacity duration-150 ${
                            openMenuProjectId === project.id
                              ? 'opacity-100 pointer-events-auto'
                              : 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus-visible:opacity-100 focus-visible:pointer-events-auto'
                          }`}
                          aria-label="Chat options"
                        >
                          <EllipsisHorizontalIcon className="h-4 w-4" style={{ color: "#234E81" }} />
                        </button>

                        {/* Context menu */}
                        {openMenuProjectId === project.id && (
                          <ChatContextMenu
                            isOpen={true}
                            onClose={() => setOpenMenuProjectId(null)}
                            position={menuPosition}
                            onRename={() => {
                              setOpenMenuProjectId(null);
                              setRenameTarget({ id: project.id, name: project.name ?? '' });
                            }}
                            onDelete={() => {
                              setOpenMenuProjectId(null);
                              setDeleteTarget({ id: project.id, name: displayName });
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </nav>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Admin/Auditing button - fixed at bottom, only visible to admins */}
      {isAdmin && conversationId && (
        <div className="px-4 py-4 border-t border-gray-200 flex-shrink-0">
          {isCollapsed ? (
            <button
              onClick={handleViewInAdmin}
              className="w-full flex items-center justify-center p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              aria-label="View in Admin"
              title="View conversation in Admin"
            >
              <BeakerIcon className="h-5 w-5 text-gray-600" />
            </button>
          ) : (
            <button
              onClick={handleViewInAdmin}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            >
              <BeakerIcon className="h-5 w-5 text-gray-600" />
              <span>View in Admin</span>
            </button>
          )}
        </div>
      )}
      {/* Modals rendered via portal to avoid CSS transform breaking fixed positioning on mobile */}
      {renameTarget && createPortal(
        <RenameChatModal
          isOpen={true}
          onClose={() => setRenameTarget(null)}
          projectId={renameTarget.id}
          currentName={renameTarget.name}
        />,
        document.body
      )}
      {deleteTarget && createPortal(
        <DeleteChatModal
          isOpen={true}
          onClose={() => setDeleteTarget(null)}
          projectId={deleteTarget.id}
          projectName={deleteTarget.name}
          onDeleted={() => {
            const deletedId = deleteTarget.id;
            setDeleteTarget(null);
            if (deletedId === currentProjectId) {
              onStartProject && onStartProject();
            }
          }}
        />,
        document.body
      )}
      {createPortal(
        <SearchChatsModal
          isOpen={isSearchModalOpen}
          onClose={() => setIsSearchModalOpen(false)}
          onSelectProject={(projectId) => {
            onProjectClick && onProjectClick(projectId);
          }}
        />,
        document.body
      )}
    </aside>
  );
}
